import { Link } from "react-router-dom";
import { RefreshCw, Sparkles, Instagram, Facebook } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Kpi, Loading, ErrorBox, Warnings, Bar } from "@/components/Widgets";
import { useApi, nf, df, COLORS } from "@/lib/api";

export default function Overview() {
  const { data, error, loading, reload } = useApi("/overview");
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const ig = data.instagram, fb = data.facebook;
  const maxEr = Math.max(...data.formats.map((f) => f.avg_er), 1);
  return (
    <>
      <PageHeader eyebrow={`Ultimi 30 giorni · aggiornato ${df(data.fetched_at)}`} title="Panoramica canali" desc={`@${ig.username || "-"} su Instagram e ${fb.name || "-"} su Facebook: crescita, portata e interazioni in un colpo d'occhio.`}>
        <Button data-testid="refresh-btn" variant="outline" className="rounded-full" onClick={() => reload({ refresh: true })}><RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Aggiorna</Button>
        <Button asChild data-testid="go-strategy-btn" className="rounded-full bg-[#2A4B3C] hover:bg-[#36604C]"><Link to="/strategia"><Sparkles className="w-4 h-4 mr-2" />Genera strategia</Link></Button>
      </PageHeader>
      <Warnings list={data.warnings} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        <Kpi testid="kpi-ig-followers" label="Follower IG" value={nf(ig.followers)} hint={`+${nf(ig.follows_30d)} in 30gg`} />
        <Kpi testid="kpi-fb-followers" label="Follower FB" value={nf(fb.followers)} hint={`${nf(fb.fan_count)} like pagina`} />
        <Kpi testid="kpi-reach" label="Copertura IG" value={nf(ig.reach_30d)} hint={`${nf(ig.views_30d)} visualizzazioni`} />
        <Kpi testid="kpi-engaged" label="Account coinvolti" value={nf(ig.accounts_engaged_30d)} hint={`${nf(ig.interactions_30d)} interazioni`} />
        <Kpi testid="kpi-er" label="Engagement medio" value={`${data.avg_er}%`} hint="interazioni / copertura" accent />
        <Kpi testid="kpi-link-taps" label="Tap link bio" value={nf(ig.profile_links_taps)} hint={`${data.posts_per_week} contenuti/settimana`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel testid="reach-chart" title="Copertura giornaliera e nuovi follower" sub="Instagram · ultimi 30 giorni" className="lg:col-span-2">
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={data.series}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2A4B3C" stopOpacity={0.25} /><stop offset="100%" stopColor="#2A4B3C" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#F1F1EC" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tickLine={false} axisLine={false} />
                <YAxis yAxisId="l" tickLine={false} axisLine={false} width={45} />
                <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} width={30} />
                <Tooltip />
                <Area yAxisId="l" type="monotone" dataKey="ig_reach" name="Copertura" stroke="#2A4B3C" strokeWidth={2} fill="url(#g1)" />
                <Area yAxisId="r" type="monotone" dataKey="ig_followers_gained" name="Nuovi follower" stroke="#C05C3D" strokeWidth={2} fill="transparent" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel testid="intent-chart" title="Intenzione nei commenti" sub="Segnali di vendita dai commenti">
          <div className="h-44">
            <ResponsiveContainer>
              <PieChart><Pie data={data.comment_intents} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} strokeWidth={2}>
                {data.comment_intents.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 mt-3">
            {data.comment_intents.map((c, i) => (
              <li key={c.name} className="flex items-center justify-between text-xs"><span className="flex items-center gap-2"><span className="w-2 h-2" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</span><span className="font-mono-num">{c.value}</span></li>
            ))}
          </ul>
        </Panel>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel testid="format-perf" title="Performance per formato" sub="Engagement rate medio">
          <div className="space-y-4">
            {data.formats.map((f) => (
              <div key={f.key}>
                <div className="flex justify-between text-sm mb-1.5"><span className="flex items-center gap-1.5">{f.platform === "instagram" ? <Instagram className="w-3.5 h-3.5" /> : <Facebook className="w-3.5 h-3.5" />}{f.format} <span className="text-[#5C5C58] text-xs">({f.count})</span></span><span className="font-mono-num">{f.avg_er}%</span></div>
                <Bar value={f.avg_er} max={maxEr} color={f.platform === "instagram" ? "#2A4B3C" : "#C05C3D"} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel testid="top-posts" title="Contenuti migliori" sub="Per interazioni totali" className="lg:col-span-2">
          <div className="divide-y divide-[#E8E8E4]">
            {data.top_posts.map((m, i) => (
              <a key={m.id} href={m.permalink} target="_blank" rel="noreferrer" data-testid={`top-post-${i}`} className="flex gap-4 py-3 group">
                <span className="font-mono-num text-[#5C5C58] text-sm w-5">{String(i + 1).padStart(2, "0")}</span>
                {m.thumbnail && <img src={m.thumbnail} alt="" className="w-12 h-12 object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate group-hover:text-[#2A4B3C]">{m.caption}</div>
                  <div className="text-xs text-[#5C5C58] mt-1">{m.platform} · {m.format} · {df(m.timestamp)}</div>
                </div>
                <div className="text-right"><div className="font-mono-num text-sm">{nf(m.interactions)}</div><div className="text-[11px] text-[#5C5C58]">{m.engagement_rate}% ER</div></div>
              </a>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}
